import { useId } from "react";
import { Box, useTheme } from "@mui/material";

const R = 38;
const CIRCUMFERENCE = 2 * Math.PI * R;

export default function ProgressArc({ current, total, label, size = 88 }) {
    const theme = useTheme();
    const w = theme.custom?.wizard ?? {};
    const fraction = total > 0 ? Math.max(0, Math.min(1, current / total)) : 0;
    const offset = CIRCUMFERENCE * (1 - fraction);
    const motion = w.motion ?? {};
    const reactId = useId();
    const gradientId = `bj-progress-arc-${reactId}`;

    return (
        <Box
            sx={{
                width: size,
                height: size,
                position: "relative",
                flexShrink: 0,
                "@media (prefers-reduced-motion: reduce)": {
                    "& svg circle": { transition: "none !important" },
                },
            }}
            role="img"
            aria-label={`Vaihe ${current} / ${total} — ${label}`}
        >
            <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: "block" }}>
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={w.amberLight ?? "#FFB36B"} />
                        <stop offset="100%" stopColor={w.amber ?? "#E8893B"} />
                    </linearGradient>
                </defs>
                <circle
                    cx="50" cy="50" r={R}
                    fill="none"
                    stroke={theme.palette.divider}
                    strokeOpacity="0.45"
                    strokeWidth="4"
                />
                <circle
                    cx="50" cy="50" r={R}
                    fill="none"
                    stroke={`url(#${gradientId})`}
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={offset}
                    transform="rotate(-90 50 50)"
                    style={{
                        filter: "drop-shadow(0 0 6px rgba(232,137,59,0.45))",
                        transition: `stroke-dashoffset ${motion.toggle ?? "220ms"} ${motion.easing ?? "cubic-bezier(0.22,1,0.36,1)"}`,
                    }}
                />
            </svg>
            <Box sx={{
                position: "absolute", inset: 0,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                pointerEvents: "none",
            }}>
                <Box sx={{
                    fontFamily: "'Black Ops One', serif",
                    fontSize: 13,
                    letterSpacing: "0.06em",
                    color: "text.primary",
                    lineHeight: 1,
                }}>{label}</Box>
                <Box sx={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 10,
                    letterSpacing: "0.10em",
                    color: "text.secondary",
                    mt: 0.5,
                }}>{current} / {total}</Box>
            </Box>
        </Box>
    );
}
